import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, FileText, Link2, ShieldCheck, Loader2 } from "lucide-react";
import { API, fileUrl } from "../lib/api";

const fmtClock = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m} min`;
};

export default function PublicValidation() {
  const { token } = useParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/tasks/${token}`)
      .then((r) => { setTask(r.data); if (r.data.validation_status === "validated" || r.data.validation_status === "refused") setDone(r.data.validation_status); })
      .catch(() => setError("Lien invalide ou expiré."))
      .finally(() => setLoading(false));
  }, [token]);

  const decide = async (decision) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/tasks/${token}/validate`, { decision, note });
      setDone(decision);
    } catch { setError("Une erreur est survenue."); } finally { setSubmitting(false); }
  };

  if (loading) return <Center><Loader2 className="animate-spin text-zinc-400" size={32} /></Center>;
  if (error) return <Center><p className="text-signal font-medium">{error}</p></Center>;

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-card rounded-lg border border-white/10 shadow-sm overflow-hidden animate-fade-in">
        <div className="bg-klein text-zinc-950 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-md bg-card flex items-center justify-center text-zinc-100 font-cabinet font-black">F</div>
            <span className="font-cabinet font-black text-xl">FlowDesk</span>
          </div>
          <p className="font-mono-label text-[11px] uppercase tracking-[0.25em] text-white/50 mb-1">Demande de validation · {task.ref}</p>
          <h1 className="font-cabinet font-bold text-2xl tracking-tight">{task.title}</h1>
          <p className="text-white/60 text-sm mt-1">De la part de {task.created_by_name}</p>
        </div>

        <div className="p-6 space-y-5">
          {task.description && <p className="text-zinc-300 whitespace-pre-wrap text-sm leading-relaxed">{task.description}</p>}

          {(task.attachments?.length > 0 || task.links?.length > 0) && (
            <div className="space-y-2">
              <p className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Pièces jointes</p>
              {task.attachments?.map((a, i) => (
                <a key={i} href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2.5 rounded-md border border-white/10 hover:border-white/30 text-sm"><FileText size={16} className="text-klein" /><span className="truncate">{a.name}</span></a>
              ))}
              {task.links?.map((l, i) => (
                <a key={i} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2.5 rounded-md border border-white/10 hover:border-white/30 text-sm"><Link2 size={16} className="text-klein" /><span className="truncate">{l.name}</span></a>
              ))}
            </div>
          )}

          {(task.budget_ht > 0 || task.time_breakdown?.length > 0) && (
            <div className="rounded-xl border border-klein/25 bg-klein/[0.06] p-4 space-y-3" data-testid="client-budget-card">
              {task.budget_ht > 0 && (
                <div>
                  <p className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-2">Budget {task.billing_mode === "temps" ? "(au temps)" : "(au forfait)"}</p>
                  <div className="flex items-center justify-between text-sm"><span className="text-zinc-400">Montant HT</span><span className="font-semibold tabular-nums">{task.budget_ht.toFixed(2)} €</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-zinc-400">TVA {task.vat_rate}%</span><span className="tabular-nums">{(task.budget_ttc - task.budget_ht).toFixed(2)} €</span></div>
                  <div className="flex items-center justify-between text-base font-bold mt-1 pt-1 border-t border-white/10"><span>Total TTC</span><span className="tabular-nums text-klein">{task.budget_ttc.toFixed(2)} €</span></div>
                </div>
              )}
              {task.time_breakdown?.length > 0 && (
                <div className="pt-1">
                  <p className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-2">Temps passé par intervenant</p>
                  {task.time_breakdown.map((m) => (
                    <div key={m.member_id} className="flex items-center justify-between text-sm py-0.5"><span className="truncate">{m.name}</span><span className="font-mono-label tabular-nums text-zinc-400">{fmtClock(m.seconds)}</span></div>
                  ))}
                </div>
              )}
            </div>
          )}

          {done ? (
            <div className={`flex flex-col items-center gap-2 py-8 ${done === "validated" ? "text-emerald-600" : "text-signal"}`}>
              {done === "validated" ? <CheckCircle2 size={56} strokeWidth={1.5} /> : <XCircle size={56} strokeWidth={1.5} />}
              <p className="font-cabinet font-bold text-xl">{done === "validated" ? "Merci, c'est validé !" : "Vous avez refusé"}</p>
              <p className="text-sm text-zinc-400">Le prestataire a été notifié.</p>
            </div>
          ) : (
            <>
              <div>
                <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Commentaire (optionnel)</label>
                <textarea data-testid="validation-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Ex: Livraison conforme, merci." className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 resize-none focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
              </div>
              <div className="flex gap-3">
                <button data-testid="public-validate-button" disabled={submitting} onClick={() => decide("validated")} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-md bg-emerald-500 text-zinc-950 font-medium hover:bg-emerald-600 disabled:opacity-60"><CheckCircle2 size={18} /> J'accepte</button>
                <button data-testid="public-refuse-button" disabled={submitting} onClick={() => decide("refused")} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-md bg-signal text-white font-medium hover:bg-red-700 disabled:opacity-60"><XCircle size={18} /> Je refuse</button>
              </div>
              <p className="flex items-center justify-center gap-1.5 text-xs text-zinc-400"><ShieldCheck size={13} /> Validation sécurisée via FlowDesk</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-[100dvh] flex items-center justify-center bg-background">{children}</div>;
}
